import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccordionModule } from 'primeng/accordion';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { EditorModule } from 'primeng/editor';

import {
  CorreoPreviewConfig,
  CorreoPreviewData,
  CorreoPreviewResult,
  itemsCorreoPreview,
} from './types/correo-preview.types';

import { EditorComponent } from 'smart-webcomponents-angular/editor';
import { EditorModule as SmartEditorModule } from 'smart-webcomponents-angular/editor';
import { UtilService } from 'src/app/services/util.service';
import { ComisionesRepository } from 'src/app/comisiones/domain/Comisiones-repository';
import { GestionCorreosRepository } from '../../servicios/gestion-correos/gestion-correos.repository';
import {
  EnvioCorreoDraftRequest,
  getCorreoHistoricoRequest,
} from '../../servicios/gestion-correos/domain/gestion-correos.request';

@Component({
  selector: 'app-correo-preview',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AccordionModule,
    InputTextModule,
    ButtonModule,
    FileUploadModule,
    ConfirmDialogModule,
    EditorModule,
    SmartEditorModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './correo-preview.component.html',
  styleUrl: './correo-preview.component.css',
})
export class CorreoPreviewComponent implements OnInit, OnDestroy {
  @ViewChild('smartEditor', { read: EditorComponent, static: false })
  smartEditor!: EditorComponent;

  protected _onDestroy = new Subject<void>();
  group: FormGroup;
  uploadedFiles: any[] = [];
  loading = false;
  idHTML = 0;
  idDraft: string = '';

  config: CorreoPreviewConfig = {
    modoLectura: false,
    mostrarBotonGuardarPlantilla: false,
    // titulo: 'Vista Preliminar',
    mostrarArchivosAdjuntos: true,
    filtrosBusquedaHistorica: false,
    tipoEnvioCorreo: 'Send',
  };
  dataCorreo: itemsCorreoPreview[] = [];

  toolbarItems: any = [
    'Bold',
    'Italic',
    'Underline',
    'StrikeThrough',
    'FontName',
    'FontSize',
    'FontColor',
    'BackgroundColor',
    'LowerCase',
    'UpperCase',
    '|',
    'Formats',
    'Alignment',
    'OrderedList',
    'UnorderedList',
    'Outdent',
    'Indent',
    '|',
    'hyperlink',
    'table',
    'Image',
    '|',
    'ClearFormat',
    'Print',
    'SourceCode',
    'splitmode',
    'FullScreen',
    '|',
    'Undo',
    'Redo',
    'subscript',
    'superscript',
    'delete',
  ];

  constructor(
    public ref: DynamicDialogRef,
    @Inject(DynamicDialogConfig) private dialogConfig: DynamicDialogConfig,
    private confirmationService: ConfirmationService,
    public util: UtilService,
    private readonly comisiones: ComisionesRepository,
    private readonly correos: GestionCorreosRepository,
  ) {
    const data = this.dialogConfig.data as CorreoPreviewData;
    if (data) {
      this.config = { ...this.config, ...data.config };
      this.dataCorreo = data.dataCorreo || [];
    }
  }

  ngOnInit(): void {
    this.initializeForm();
    this.cargarDatosIniciales();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private initializeForm(): void {
    const body = this.dataCorreo?.[0]?.body || '';
    this.group = new FormGroup({
      correo: new FormControl(body),
      mailBcc: new FormControl(null),
      mailCc: new FormControl(null),
      mailTo: new FormControl(null),
      subject: new FormControl(null),
      mailFrom: new FormControl(null),
      asunto_orig: new FormControl(null),
    });
  }

  private cargarDatosIniciales(): void {
    if (this.dataCorreo && this.dataCorreo.length > 0) {
      const item = this.dataCorreo[0];
      const bodyFormateado = this.formatearSaltosDeLinea(item?.body || '');
      setTimeout(() => {
        if (!this.config.modoLectura && this.smartEditor) {
          this.smartEditor.value = item?.body || '';
        }
      }, 100);
      this.group.get('mailBcc')?.setValue(item?.mailBcc || '');
      this.group.get('mailCc')?.setValue(item?.mailCc || '');
      this.group.get('mailTo')?.setValue(item?.mailTo || '');
      this.group.get('subject')?.setValue(item?.subject || '');
      this.group.get('mailFrom')?.setValue(item?.mailFrom || '');
      this.idHTML = item?.idHTML || 0;
      this.uploadedFiles = item?.filesData || [];
    }
  }

  correoValid(): boolean {
    if (this.config.modoLectura) return true;
    if (!this.smartEditor || !this.smartEditor.value) return false;
    const content = this.smartEditor.value.trim();
    return (
      content !== '' &&
      content !== '<p><br></p>' &&
      content !== '<p><strong></strong></p><p></p>'
    );
  }

  salir(): void {
    this.ref.close({ accion: 'salir' } as CorreoPreviewResult);
  }

  enviarCorreo(): void {
    this.confirmationService.confirm({
      message: '¿Está seguro que desea enviar el correo?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const archivoscontenidos = this.uploadedFiles.map((file) => file.item2);
        const archivosnombres = this.uploadedFiles.map((file) => file.item1);
        const body = this.config.modoLectura ? this.group.get('correo')?.value : this.smartEditor.value;

        const request = {
          destinatarios: this.group.value['mailTo'],
          asunto_reunion: this.group.value['subject'],
          tipo: null,
          correoorigen: this.group.value['mailFrom'],
          copia: this.group.value['mailCc'],
          copiaoculta: this.group.value['mailBcc'],
          body: body,
          subject: this.group.value['subject'],
          fileContents: archivoscontenidos,
          fileNames: archivosnombres,
          bytesAtchivo: archivoscontenidos,
          nombrearchivos: archivosnombres,
        };

        if (this.config.tipoEnvioCorreo == 'Personalizado') {
          this.ref.close({
            accion: 'enviar',
            datos: request,
          } as CorreoPreviewResult);
        } else if (this.config.tipoEnvioCorreo == 'Draft' && this.idDraft != '') {
          const request_draft = <EnvioCorreoDraftRequest>{};
          request_draft.datosCorreo = request;
          request_draft.idDraft = this.idDraft;
          this.correos.envioCorreoDraft(request_draft).subscribe(
            (response) => {
              if (response.datos.status == 200) {
                this.util.MessageDialog({titulo: 'Correo Enviado',mensaje: response.meta.mensaje,tipo: 'ok' });
                this.salir();
              }
            },
            (err) => {
              const mensaje = err?.error?.meta?.mensaje || (err?.error?.errors? Object.values(err.error.errors).flat().join(' | '): 'Error');
              this.util.MessageDialog({ titulo: 'ERROR', mensaje: mensaje, tipo: 'warning' });
            },
          );
        } else {
          this.comisiones.EnviarCorreo2(request).subscribe(
            (response) => {
              if (response.datos.status == 200) {
                this.ref.close({
                  accion: 'enviar',
                  datos: response,
                } as CorreoPreviewResult);
                this.util.MessageDialog({titulo: 'Correo Enviado',mensaje: response.meta.mensaje,tipo: 'ok' });
                this.salir();
              } else {
                this.util.MessageDialog({ titulo: 'ERROR', mensaje: 'No se envió el correo', tipo: 'error'});
              }
            },
            (err) => {
              const mensaje = err?.error?.meta?.mensaje || (err?.error?.errors ? Object.values(err.error.errors).flat().join(' | ') : 'Error');
              this.util.MessageDialog({titulo: 'ERROR', mensaje: mensaje, tipo: 'warning' });
            },
          );
        }
      },
      reject: () => {},
    });
  }

  guardarPlantilla(): void {
    this.confirmationService.confirm({
      message: '¿Está seguro que desea actualizar la plantilla?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const html = this.config.modoLectura ? this.group.get('correo')?.value : this.smartEditor.value;
        this.ref.close({
          accion: 'guardar',
          datos: html,
        } as CorreoPreviewResult);
      },
      reject: () => {},
    });
  }

  onUpload(event: any, fileUpload: any): void {
    if (this.config.modoLectura) return;
    event.files.forEach((file: File) => {
      const reader = new FileReader();
      reader.onload = ({ target }) => {
        this.uploadedFiles.push({
          item1: file.name,
          item2: (target?.result as string).split(',')[1],
          item3: this.formatFileSize(file.size),
        });
      };
      reader.readAsDataURL(file);
    });
    fileUpload.clear();
  }

  formatFileSize(size: number): string {
    const units = ['bytes', 'KB', 'MB'];
    const index = Math.min(
      Math.floor(Math.log(size) / Math.log(1024)),
      units.length - 1,
    );
    return `${(size / Math.pow(1024, index)).toFixed(2)} ${units[index]}`;
  }

  downloadFile(file: any): void {
    if (!file?.item2) return;
    const extension = file.item1.split('.').pop()?.toLowerCase() || '';
    const mimeType = this.util.getMimeType(extension);
    const byteArray = Uint8Array.from(atob(file.item2), (char) =>
      char.charCodeAt(0),
    );
    const blob = new Blob([byteArray], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.item1;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private formatearSaltosDeLinea(texto: string): string {
    if (!texto) return '';

    return texto
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n/g, '<br>');
  }

  private formatearTextoCompleto(texto: string): string {
    if (!texto) return '';

    let formateado = texto.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    formateado = formateado.replace(/  /g, ' &nbsp;');
    formateado = formateado.replace(/\n/g, '<br>');
    return formateado;
  }

  searchCorreoHistorico() {
    const loadingScreen = this.util.showLoadingScreen({
      message: 'Obteniendo datos ...',
    });
    try {
      const request = <getCorreoHistoricoRequest>{};
      request.asunto_reunion = this.group.value.asunto_orig;
      request.subject = this.group.value.asunto_orig;
      request.correoorigen = this.group.value.mailFrom;
      request.destinatarios = this.group.value.mailFrom;
      request.copia = this.group.value.mailFrom;
      request.copiaoculta = this.group.value.mailFrom;
      request.fileContents = [];
      request.fileNames = [];
      request.bytesAtchivo = [];
      request.nombrearchivos = [];

      this.correos.obtenerCorreoHistorico(request).subscribe(
        (response) => {
          if (response.datos.status == 200) {
            loadingScreen.close();
            const item = response.datos.result;
            this.idDraft = item?.id;
            const joinEmails = (...parts: any[]) => {
              return parts.flat().filter(Boolean).join(';');
            };
            const values = this.group.value;
            this.group.get('mailBcc')?.setValue(joinEmails(values['mailBcc'], item?.copiaOculta));
            this.group.get('mailCc')?.setValue(joinEmails(values['mailCc'], item?.copia));
            this.group.get('mailTo')?.setValue(
                joinEmails(
                  values['mailTo'],
                  item?.remitenteCorreo,
                  item?.destinatarios,
                ),
              );
            if (item?.body) {
              if (!this.config.modoLectura && this.smartEditor) {
                this.smartEditor.value = (this.smartEditor.value || '') + item.body;
              } else {
                this.group.get('correo')?.setValue((values['correo'] || '') + item.body);
              }
            }
          }
        },
        (err) => {
          loadingScreen.close();
          const mensaje = err?.error?.meta?.mensaje || (err?.error?.errors? Object.values(err.error.errors).flat().join(' | ') : 'Error');
          this.util.MessageDialog({ titulo: 'ERROR', mensaje: mensaje, tipo: 'warning' });
        },
      );
    } catch (err) {
      loadingScreen.close();
      const mensaje = err?.error?.meta?.mensaje || (err?.error?.errors? Object.values(err.error.errors).flat().join(' | ') : 'Error');
      this.util.MessageDialog({ titulo: 'ERROR', mensaje: mensaje, tipo: 'warning' });
    } finally {
      loadingScreen.close();
    }
  }

}
